/**
 * Pure view model for the pool search result list (ISSUE-093).
 *
 * Input is `/pool/search`'s response items. The server has already applied
 * the query text, the sport/league/pool-type filters, and the visibility
 * rule (server-resolves-client-renders.mdc: the server decides what is
 * findable). This module renders exactly what it is given. It never
 * re-filters, re-sorts, or hides a row itself.
 */
import { memberCountLabel } from "./poolsListState";

export type typePoolSearchRow = {
  poolId: string;
  // Matches the generated `typePoolSearchOutput200` item shape: optional AND
  // nullable, per the api-surface deviation note (`poolRefId` is nullable on
  // the wire because ISSUE-094 ships it nullable this release).
  poolRefId?: string | null;
  name: string;
  sportLabel: string;
  leagueLabel: string;
  visibility: "public" | "private";
  activeMemberCount: number;
  isMember: boolean;
  hasPendingInvite: boolean;
};

export type typePoolSearchResultModel = {
  poolId: string;
  name: string;
  poolRefId: string | null;
  sportLabel: string;
  leagueLabel: string;
  membersLabel: string;
  isPrivate: boolean;
  isMember: boolean;
  hasPendingInvite: boolean;
};

export type typePoolSearchState =
  | { phase: "idle" }
  | { phase: "loading" }
  | { phase: "failed" }
  | { phase: "noResults"; query: string }
  | { phase: "ready"; results: typePoolSearchResultModel[] };

// Below this, a keystroke on a fresh search box would fire a global search
// on every letter typed. Two matches the same floor
// InviteSheet.tsx uses for /invite/user-search (ISSUE-189), the platform's
// other type-ahead search box.
export const POOL_SEARCH_MIN_QUERY_LENGTH = 2;

export function toPoolSearchResultModel(
  row: typePoolSearchRow,
): typePoolSearchResultModel {
  return {
    poolId: row.poolId,
    name: row.name,
    poolRefId: row.poolRefId ?? null,
    sportLabel: row.sportLabel,
    leagueLabel: row.leagueLabel,
    membersLabel: memberCountLabel(row.activeMemberCount),
    isPrivate: row.visibility === "private",
    isMember: row.isMember,
    hasPendingInvite: row.hasPendingInvite,
  };
}

export function resolvePoolSearchState(input: {
  trimmedQuery: string;
  isLoading: boolean;
  hasFailed: boolean;
  items: readonly typePoolSearchRow[] | null;
}): typePoolSearchState {
  if (input.trimmedQuery.length < POOL_SEARCH_MIN_QUERY_LENGTH) {
    return { phase: "idle" };
  }
  if (input.hasFailed) {
    return { phase: "failed" };
  }
  if (input.isLoading) {
    return { phase: "loading" };
  }
  if (input.items === null || input.items.length === 0) {
    return { phase: "noResults", query: input.trimmedQuery };
  }
  return {
    phase: "ready",
    results: input.items.map(toPoolSearchResultModel),
  };
}
